import React from 'react';
import { createPortal } from 'react-dom';
import delve from 'dlv';

import detectIt from 'detect-it';

import getEntityKey from 'helpers/getEntityKey';
import TrackerTooltipItem from './TrackerTooltipItem';
import {
  ISelectedEntityPropType,
  IExtendedSelectedEntityPropType,
} from '../types';

const { hasTouch } = detectIt;

interface ITrackerTooltipProps {
  tooltipRoot?: { current: Element };
  /** An object with the active trends for this tracker */
  trends: ISelectedEntityPropType[];
  allEntities?: any;
  /** A d3-scale ordinal color scale */
  colorScale: (key: string) => any;
  /** A configured d3-scale function */
  x: (key: any) => any;
  /** A configured d3-scale function */
  y: (key: any) => any;
  /** The key that represents the data value in the object coming from the API */
  dataKey: string;
  /** Margins of the LineChart */
  margin: any;
  /** The label of the type of data. E.g. streams / downloads */
  label: string;
  /** Wether or not a touch user is currently dragging the tracker */
  isDragging: boolean;
  height: number;
  weekNumber: number;
}

/** This renders Tooltip components inside portals for every trendline */
const TrackerTooltip: React.FC<ITrackerTooltipProps> = (props) => {
  const {
    tooltipRoot,
    trends,
    allEntities,
    x,
    y,
    colorScale,
    dataKey,
    margin,
    label,
    isDragging,
    height,
    weekNumber,
  } = props;

  /** Only show the tooltip if a touch user is actually dragging the Tracker */
  if (hasTouch && !isDragging) {
    return null;
  }

  const sortedTrends = [...trends].sort((a, b) => b.data.value - a.data.value);

  if (tooltipRoot && tooltipRoot.current) {
    return createPortal(
      trends.map((trend) => {
        const yValue = delve(trend.data, dataKey);
        const xCoord = Math.round(
          x(trend.data?.index ? weekNumber : trend.data.date)
        );
        const yCoord = Math.round(y(yValue));
        const fillColor = colorScale(getEntityKey(trend));

        if (yValue == null) {
          return null;
        }

        const trackerLine = sortedTrends[trends.length === 1 ? 0 : 1].breakdown;

        const topPosition = yCoord + margin.top;

        if (
          allEntities &&
          (trackerLine === trend.breakdown || trends.length === 1)
        ) {
          const sumValue = sortedTrends.reduce(
            (sum, trend) => sum + trend.data.value,
            0
          );

          const { labels, yValue, dotColors, percentageValue } =
            sortedTrends.reduce(
              (
                prev: IExtendedSelectedEntityPropType,
                curr: ISelectedEntityPropType
              ): IExtendedSelectedEntityPropType => {
                const labels = [...prev.labels, curr.breakdown.toString()];
                const yValue = [...prev.yValue, curr.data.value];
                const dotColors = [
                  ...prev.dotColors,
                  colorScale(getEntityKey(curr)),
                ];
                const percentageValue = [
                  ...prev.percentageValue,
                  // curr.data.percentage || curr.data.value / sumValue,
                  curr.data.value / sumValue, // TODO: use row above when Percentages on BE will be fixed
                ];

                return { labels, yValue, dotColors, percentageValue };
              },
              { labels: [], yValue: [], dotColors: [], percentageValue: [] }
            );

          return (
            <TrackerTooltipItem
              key={`tooltip-${getEntityKey(trend)}`}
              tooltipRoot={tooltipRoot}
              left={xCoord + margin.left}
              top={height - topPosition > 40 ? topPosition : height - 35}
              color={'#001247'}
              labels={labels}
              yValue={yValue}
              secondValue={percentageValue}
              hasTouch={hasTouch}
              dotColors={dotColors}
              type="orgVsProg"
              isFormattedNumber={false}
            />
          );
        } else if (allEntities && trackerLine !== trend.breakdown) {
          return null;
        }

        return (
          <TrackerTooltipItem
            key={`tooltip-${getEntityKey(trend)}`}
            tooltipRoot={tooltipRoot}
            left={xCoord + margin.left}
            top={height - topPosition > 10 ? topPosition : height - 15}
            color={fillColor}
            labels={[label]}
            yValue={yValue}
            hasTouch={hasTouch}
            isFormattedNumber={false}
          />
        );
      }),
      tooltipRoot.current
    );
  }

  return null;
};

export default TrackerTooltip;
